import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Action, Text, colors } from '@soluto-private/mx-asurion-ui-react';

import { useAnalytics } from '@service-initiation/shared-data-access-analytics';
import {
  useCurrentPath,
  useGoToStep,
} from '@service-initiation/shared-data-access-flow-manager';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import {
  useCompanyNameValue,
  useDeviceDetailsAsyncAtom,
  useResetDeviceAndDamageState,
  useSelectedRepairsAsyncAtom,
} from '@service-initiation/tech-care-data-access-store';
import { getDeviceTypeName } from '@service-initiation/tech-care-data-access-tech-care-services';

import defaultRepairImage from '../assets/default-repair-image.png';
import { SectionContainer, SectionTitle } from '../components';

const Image = styled.img`
  width: 80px;
  min-height: 80px;
  object-fit: contain;
`;

const LeftContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  flex: 1;
`;

const SummaryDetailContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
`;

const SummaryDetailContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  justify-content: center;
  gap: 2px;
`;

const SummaryDamageText = styled(Text)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @supports (-webkit-line-clamp: 2) {
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const TEXT = TranslationEntries.Repairs.Cart.Summary;

export const SummarySection: FC = () => {
  const { t } = useTranslation();
  const { value: deviceDetails } = useDeviceDetailsAsyncAtom();
  const { value: selectedRepairs } = useSelectedRepairsAsyncAtom();
  const companyName = useCompanyNameValue();
  const currentPath = useCurrentPath();
  const { dispatchClickEvent } = useAnalytics();
  const { goToStep } = useGoToStep();
  const { resetDeviceAndDamageState } = useResetDeviceAndDamageState();

  return (
    <SectionContainer skeletonHeight="116px">
      <SectionTitle>{t(TEXT.Title)}</SectionTitle>
      <SummaryDetailContainer>
        <LeftContentContainer>
          <Image
            alt="Device image"
            src={
              !deviceDetails?.imageLink
                ? defaultRepairImage
                : deviceDetails?.imageLink
            }
          />
          <SummaryDetailContentContainer>
            <SummaryDamageText size={2} weight="heavy">
              {selectedRepairs && selectedRepairs.length > 0
                ? selectedRepairs
                    .map(({ repairType: { symptom } }) => symptom)
                    ?.join(', ')
                : companyName || t(TEXT.PrimaryText)}
            </SummaryDamageText>
            <Text color={colors.neutralDeepest} size={1} weight="base">
              {deviceDetails?.name
                ? getDeviceTypeName(deviceDetails?.name)
                : t(TEXT.SecondaryText)}
            </Text>
          </SummaryDetailContentContainer>
        </LeftContentContainer>
        {deviceDetails &&
          ![Route.Confirmation, Route.DeviceType].includes(
            currentPath as Route
          ) && (
            <Action
              color="secondary"
              data-testid="edit-DeviceType-link"
              onClick={() => {
                dispatchClickEvent('editDeviceTypeClick', {
                  Cta: t(TranslationEntries.Repairs.Nav.Edit),
                  LinkTo: 'DeviceType',
                });
                resetDeviceAndDamageState();
                goToStep(Route.DeviceType);
              }}
              variant="flat"
            >
              {t(TranslationEntries.Repairs.Nav.Edit)}
            </Action>
          )}
      </SummaryDetailContainer>
    </SectionContainer>
  );
};
